import type { Cell } from '../../sim';
import { tallyText, trainedText } from './text';
import {
  ATTACK_MERGE_RADIUS,
  ATTACK_PRIORITY,
  type GameEvent,
  hexDistance,
  type LossTally,
  type Notification,
  type NotificationKind,
} from './types';

/**
 * The feed: events folded into entries (attacks by area, arrivals and
 * losses rolling up, idle workers by city), entries expiring, the banners
 * and edge alerts taken from it, and dismissing.
 */

/** Seconds an attack notification stays open after the last hit. */
export const ATTACK_LINGER = 10;
/** Seconds other notifications stay up. */
const NOTIFICATION_LIFETIME = 12;
/** Seconds the rolling arrivals entry stays up after the latest arrival. */
const ARRIVAL_LIFETIME = 8;
/** Seconds since the last hit for an attack to count as ongoing. */
export const ATTACK_ONGOING = 3;
/** Most entries the feed shows (dismissed attacks, kept hidden, and losses, kept until dismissed, don't count). */
export const MAX_NOTIFICATIONS = 6;

/** Seconds good news (an upgrade finished, a city era) stays up. */
const NEWS_LIFETIME = 30;

function isExpired(n: Notification, now: number): boolean {
  // Conditions end when their key stops being active (see addEvents), not on a timer.
  if (n.key !== undefined) return false;
  // Losses (a city, a barracks) stay until the player dismisses them; a
  // dismissed one stays hidden while losses there keep coming (see mergeTarget).
  if (n.kind === 'loss') return !!n.dismissed && now - n.lastTime > ATTACK_LINGER;
  if (n.kind === 'attack') return now - n.lastTime > ATTACK_LINGER;
  if (n.kind === 'arrival' || n.kind === 'trained') return now - n.lastTime > ARRIVAL_LIFETIME;
  if (n.kind === 'upgraded' || n.kind === 'era') return now - n.time > NEWS_LIFETIME;
  return now - n.time > NOTIFICATION_LIFETIME;
}

function attackRank(e: GameEvent): number {
  return e.priority ?? (e.unitLost ? ATTACK_PRIORITY.unitLost : ATTACK_PRIORITY.unit);
}

/** Index of the existing notification `e` (at sim time `now`) should be folded into, or -1. */
function mergeTarget(list: readonly Notification[], e: GameEvent, now: number): number {
  const pos = e.pos;
  if (!pos) return -1;
  if (e.kind === 'attack') {
    return list.findIndex(
      (n) =>
        n.kind === 'attack' &&
        n.pos !== undefined &&
        hexDistance(n.pos, pos) <= ATTACK_MERGE_RADIUS &&
        // A lost unit never hides behind a bigger hit (a city): it gets its own entry.
        (!e.unitLost || attackRank(n) <= attackRank(e))
    );
  }
  if (e.kind === 'idle') {
    return list.findIndex((n) => n.kind === 'idle' && !n.dismissed && n.cityId === e.cityId);
  }
  if (e.kind === 'loss' && e.tally) {
    const what = e.tally.what;
    // Into a dismissed one too, which stays dismissed: one rolling entry per area.
    return list.findIndex(
      (n) =>
        n.kind === 'loss' &&
        n.tally?.what === what &&
        n.pos !== undefined &&
        hexDistance(n.pos, pos) <= ATTACK_MERGE_RADIUS &&
        now - n.lastTime <= ATTACK_LINGER
    );
  }
  if (e.kind === 'arrival') {
    // One rolling entry for every arrival.
    return list.findIndex((n) => n.kind === 'arrival' && !n.dismissed);
  }
  if (e.kind === 'trained') {
    // One rolling entry per city.
    return list.findIndex((n) => n.kind === 'trained' && !n.dismissed && n.cityId === e.cityId);
  }
  return -1;
}

/**
 * Adds `events` at sim time `now` and drops expired entries. Repeated hits in
 * one area update a single attack notification (its ⌖ follows the latest
 * hit), every arrival folds into one rolling entry ("3 units arrived", at
 * the latest unit's tile, gone ARRIVAL_LIFETIME after the last one), units
 * done training fold into one rolling entry per city ("5 Warriors trained
 * in City 1", gone ARRIVAL_LIFETIME after the last one), and a
 * city's idle-worker count updates its entry, and losses of units (or of
 * buildings) within ATTACK_MERGE_RADIUS of an open loss entry of the same
 * kind, its last loss under ATTACK_LINGER s ago, add to its tally ("5 units
 * lost near City 1: 3 Warriors, 2 Archers"), instead of stacking up. An
 * attack entry takes the text of the most important hit in its area (an own
 * city over other buildings over units; the latest among equals) and the
 * attackers last named there (a hit with none in sight keeps them); a more important hit reopens an entry the
 * player had dismissed. Once an own city (or other building) is reported
 * lost, the attack entry about it goes, so it doesn't still read "under
 * attack". A killed scout folds into the attack entry there,
 * which takes its text (and reopens), unless that entry reports a hit own
 * city: then the loss gets its own entry. At
 * Conditions (events with a `key`, such as food running out) keep one entry
 * per key, updated in place while an event with its key arrives every frame
 * and dropped on the first frame without one; dismissing one hides it until
 * it clears. At
 * most MAX_NOTIFICATIONS other entries are shown: older ones drop off first
 * (losses stay until dismissed and don't count toward the cap).
 */
export function addEvents(
  list: readonly Notification[],
  events: readonly GameEvent[],
  now: number,
  nextId: () => number
): Notification[] {
  // A condition (an entry with a key) with no event this frame has cleared and
  // is gone, dismissed or not: it raises afresh the next time it holds.
  const active = new Set(events.flatMap((e) => (e.key === undefined ? [] : [e.key])));
  let result = list.filter((n) => !isExpired(n, now) && (n.key === undefined || active.has(n.key)));
  for (const e of events) {
    if (e.kind === 'loss' && e.entityId !== undefined) {
      const gone = e.entityId;
      result = result.filter((n) => n.kind !== 'attack' || n.entityId !== gone);
    }
    if (e.key !== undefined) {
      result = raiseCondition(result, e, now, nextId);
      continue;
    }
    const i = mergeTarget(result, e, now);
    if (i >= 0) {
      const n = result[i];
      const count = n.count + 1;
      if (e.kind === 'loss' && e.tally && n.tally) {
        result[i] = mergeLoss(n, e.tally, count, now, e.pos);
        continue;
      }
      if (e.kind === 'trained' && e.trained && n.trained) {
        const counts = { ...n.trained.counts };
        for (const [name, k] of Object.entries(e.trained.counts)) {
          counts[name] = (counts[name] ?? 0) + k;
        }
        const trained = { counts, city: e.trained.city };
        result[i] = {
          ...n,
          count,
          time: now,
          lastTime: now,
          trained,
          text: trainedText(trained),
          ...(e.pos ? { pos: e.pos } : {}),
        };
        continue;
      }
      result[i] =
        e.kind === 'arrival'
          ? {
              ...n,
              count,
              time: now,
              lastTime: now,
              text: `${count} units arrived`,
              ...(e.pos ? { pos: e.pos } : {}),
            }
          : e.kind === 'idle'
            ? { ...n, count, time: now, lastTime: now, text: e.text, idle: e.idle }
            : {
                ...n,
                count,
                lastTime: now,
                // A hit at least as important (a city over a farm or a unit)
                // takes the entry over: its text, what was hit and where.
                ...(attackRank(e) >= attackRank(n)
                  ? {
                      text: e.text,
                      priority: attackRank(e),
                      entityId: e.entityId,
                      ...(e.pos ? { pos: e.pos } : {}),
                    }
                  : {}),
                // A lost unit, or a more important hit (a city after a farm),
                // reopens the entry, even where the attack was closed.
                ...(e.unitLost ? { unitLost: true } : {}),
                ...(e.unitLost || attackRank(e) > attackRank(n) ? { dismissed: false } : {}),
                // Names stay until a later hit sees attackers (ones out of
                // sight, or already dead, don't clear them).
                ...(e.attackers ? { attackers: e.attackers } : {}),
                target: e.target?.id ? e.target : (n.target ?? e.target),
              };
      continue;
    }
    result.push({ ...e, id: nextId(), time: now, lastTime: now, count: 1 });
  }
  return capFeed(result);
}

/**
 * A condition event: updates the entry with its key in place (keeping its
 * place in the stack and whether the player dismissed it), else adds one.
 */
function raiseCondition(
  list: Notification[],
  e: GameEvent,
  now: number,
  nextId: () => number
): Notification[] {
  const i = list.findIndex((n) => n.key === e.key);
  if (i < 0) return [...list, { ...e, id: nextId(), time: now, lastTime: now, count: 1 }];
  const n = list[i];
  const same =
    n.text === e.text &&
    n.detail === e.detail &&
    n.badge === e.badge &&
    n.progress === e.progress &&
    n.cityId === e.cityId &&
    n.action === e.action &&
    n.pos?.q === e.pos?.q &&
    n.pos?.r === e.pos?.r;
  if (same) return list;
  const next = list.slice();
  next[i] = {
    ...n,
    text: e.text,
    detail: e.detail,
    badge: e.badge,
    progress: e.progress,
    cityId: e.cityId,
    action: e.action,
    pos: e.pos,
    lastTime: now,
  };
  return next;
}

/** A rolling loss entry with one more loss added: counts summed, at its place and time. */
function mergeLoss(
  n: Notification,
  add: LossTally,
  count: number,
  now: number,
  pos?: Cell
): Notification {
  const prev = n.tally as LossTally;
  const counts = { ...prev.counts };
  for (const [name, k] of Object.entries(add.counts)) counts[name] = (counts[name] ?? 0) + k;
  const groups = [...new Set([...(prev.groups ?? []), ...(add.groups ?? [])])];
  const groupNames = { ...prev.groupNames, ...add.groupNames };
  const tally: LossTally = {
    what: prev.what,
    counts,
    ...(groups.length > 0 ? { groups } : {}),
    ...(Object.keys(groupNames).length > 0 ? { groupNames } : {}),
    place: add.place,
  };
  return {
    ...n,
    count,
    time: now,
    lastTime: now,
    tally,
    text: tallyText(tally),
    ...(pos ? { pos } : {}),
  };
}

/**
 * The newest MAX_NOTIFICATIONS shown entries, plus every dismissed (hidden)
 * one and every loss (they stay until dismissed, see lossBanner, and take no
 * room from the rest).
 */
function capFeed(list: Notification[]): Notification[] {
  const exempt = (n: Notification) => n.dismissed || n.kind === 'loss' || n.key !== undefined;
  let shown = list.filter((n) => !exempt(n)).length;
  if (shown <= MAX_NOTIFICATIONS) return list;
  return list.filter((n) => {
    if (exempt(n) || shown <= MAX_NOTIFICATIONS) return true;
    shown--;
    return false;
  });
}

/** Most loss entries the stack lists; older ones fold into "+N older losses" (see stackView). */
export const MAX_FEED_LOSSES = 3;

/**
 * The red banner under the timer: the latest loss (a city or a barracks) and
 * how many other losses are still unacknowledged. It stays until the player
 * dismisses it or clicks it, so a loss can't pass unseen while they look
 * elsewhere.
 */
export function lossBanner(
  list: readonly Notification[]
): { latest: Notification; others: number } | null {
  const losses = list.filter((n) => n.kind === 'loss' && !n.dismissed);
  if (losses.length === 0) return null;
  const latest = losses.reduce((a, n) => (n.time >= a.time ? n : a));
  return { latest, others: losses.length - 1 };
}

/**
 * Closes a notification. Attacks, losses and conditions stay hidden (see
 * Notification.dismissed), so more of the same in that area doesn't raise
 * them again; anything else goes.
 */
export function dismiss(list: readonly Notification[], id: number): Notification[] {
  return list.flatMap((n) => {
    if (n.id !== id) return [n];
    return n.kind === 'attack' || n.kind === 'loss' || n.key !== undefined
      ? [{ ...n, dismissed: true }]
      : [];
  });
}

/** Seconds a loss keeps its edge arrow (the banner stays until dismissed). */
export const URGENT_LOSS_ARROW = 15;

/** An urgent notification with a place: the screen edge glows for it and an arrow points there. */
export interface UrgentAlert {
  id: number;
  kind: NotificationKind;
  text: string;
  pos: Cell;
  lastTime: number;
}

/**
 * The notifications urgent right now, newest first: attacks on the player's
 * things until they expire, losses for URGENT_LOSS_ARROW s, and urgent spy
 * events (an own spy exposed) for their lifetime; dismissed ones left out.
 */
export function urgentAlerts(list: readonly Notification[], now: number): UrgentAlert[] {
  const out: UrgentAlert[] = [];
  for (const n of list) {
    if (n.dismissed || !n.pos) continue;
    const urgent =
      n.kind === 'attack'
        ? now - n.lastTime <= ATTACK_LINGER
        : n.kind === 'loss'
          ? now - n.time <= URGENT_LOSS_ARROW
          : n.urgent === true && now - n.time <= NOTIFICATION_LIFETIME;
    if (urgent)
      out.push({ id: n.id, kind: n.kind, text: n.text, pos: n.pos, lastTime: n.lastTime });
  }
  return out.sort((a, b) => b.lastTime - a.lastTime);
}

/**
 * The alerts that pulse the edge glow now: ones not seen before, and attacks
 * flaring up again after a lull of more than ATTACK_ONGOING s. `seen` (alert
 * id → its last time seen) is updated in place.
 */
export function urgentPulses(
  seen: Map<number, number>,
  alerts: readonly UrgentAlert[]
): UrgentAlert[] {
  const pulses: UrgentAlert[] = [];
  for (const a of alerts) {
    const before = seen.get(a.id);
    if (before === undefined || a.lastTime - before > ATTACK_ONGOING) pulses.push(a);
    seen.set(a.id, a.lastTime);
  }
  const ids = new Set(alerts.map((a) => a.id));
  for (const id of seen.keys()) if (!ids.has(id)) seen.delete(id);
  return pulses;
}
